import { serializeAsJSON } from "@excalidraw/excalidraw/data/json";
import { isInitializedImageElement } from "@excalidraw/element";

import type {
  NonDeletedExcalidrawElement,
  FileId,
} from "@excalidraw/element/types";
import type { AppState, BinaryFiles } from "@excalidraw/excalidraw/types";

import { api } from "./apiClient";
import { setCurrentCloudDoc } from "./currentDoc";
import { putToPresigned, getTextFromPresigned } from "./S3FileManager";
import { generateThumbnail } from "./thumbnails";

import type {
  CloudDocSummary,
  CloudFolder,
  CloudVersion,
  CreateDocResponse,
  GetDocResponse,
  ResolveShareResponse,
  ShareResponse,
  UpdateDocResponse,
  VersionType,
} from "./types";

const referencedFileIds = (
  elements: readonly NonDeletedExcalidrawElement[],
  files: BinaryFiles,
): FileId[] => {
  const ids = new Set<FileId>();
  for (const el of elements) {
    if (isInitializedImageElement(el) && files[el.fileId]) {
      ids.add(el.fileId);
    }
  }
  return [...ids];
};

const uploadContent = async (
  uploads: { scene: string; thumb: string; files: Record<string, string> },
  scene: string,
  thumb: Blob,
  files: BinaryFiles,
) => {
  await Promise.all([
    putToPresigned(uploads.scene, scene, "application/json"),
    putToPresigned(uploads.thumb, thumb, "image/png"),
    ...Object.entries(uploads.files).map(([fileId, url]) =>
      putToPresigned(
        url,
        files[fileId as FileId].dataURL,
        "application/octet-stream",
      ),
    ),
  ]);
};

export const cloudService = {
  // ---- folders ----
  async listFolders(): Promise<CloudFolder[]> {
    const res = await api.get<{ folders: CloudFolder[] }>("/folders");
    return res.folders;
  },
  async createFolder(folderPath: string): Promise<void> {
    await api.post("/folders", { folderPath });
  },
  async deleteFolder(path: string, recursive: boolean): Promise<void> {
    const q = `?path=${encodeURIComponent(path)}${
      recursive ? "&recursive=true" : ""
    }`;
    await api.del(`/folders${q}`);
  },

  // ---- documents ----
  async listDocs(folder: string): Promise<CloudDocSummary[]> {
    const res = await api.get<{ documents: CloudDocSummary[] }>(
      `/docs?folder=${encodeURIComponent(folder)}`,
    );
    return res.documents;
  },

  /** Create a brand-new cloud document (first save / Save as). */
  async createDoc(
    name: string,
    folderPath: string,
    elements: readonly NonDeletedExcalidrawElement[],
    appState: Partial<AppState>,
    files: BinaryFiles,
  ): Promise<{ docId: string; versionId: string }> {
    const fileIds = referencedFileIds(elements, files);
    const res = await api.post<CreateDocResponse>("/docs", {
      name,
      folderPath,
      fileIds,
    });
    const scene = serializeAsJSON(elements, appState, files, "database");
    const thumb = await generateThumbnail(elements, appState, files);
    await uploadContent(res.uploads, scene, thumb, files);
    setCurrentCloudDoc({
      docId: res.docId,
      name,
      folderPath,
      currentVersionId: res.versionId,
      autosave: false,
    });
    return { docId: res.docId, versionId: res.versionId };
  },

  /** Save a new version of an existing cloud document. */
  async updateDoc(
    docId: string,
    type: VersionType,
    description: string | null,
    elements: readonly NonDeletedExcalidrawElement[],
    appState: Partial<AppState>,
    files: BinaryFiles,
    autosaveFlag?: boolean,
  ): Promise<{ versionId: string }> {
    const fileIds = referencedFileIds(elements, files);
    const res = await api.put<UpdateDocResponse>(`/docs/${docId}`, {
      type,
      description,
      fileIds,
      autosave: autosaveFlag,
    });
    const scene = serializeAsJSON(elements, appState, files, "database");
    const thumb = await generateThumbnail(elements, appState, files);
    await uploadContent(res.uploads, scene, thumb, files);
    return { versionId: res.versionId };
  },

  async getDoc(docId: string): Promise<GetDocResponse> {
    return api.get<GetDocResponse>(`/docs/${docId}`);
  },

  async renameDoc(docId: string, name: string): Promise<void> {
    await api.patch(`/docs/${docId}`, { name });
  },
  async moveDoc(docId: string, folderPath: string): Promise<void> {
    await api.patch(`/docs/${docId}`, { folderPath });
  },
  async deleteDoc(docId: string): Promise<void> {
    await api.del(`/docs/${docId}`);
  },

  // ---- versions ----
  async listVersions(docId: string): Promise<CloudVersion[]> {
    const res = await api.get<{ versions: CloudVersion[] }>(
      `/docs/${docId}/versions`,
    );
    return res.versions;
  },
  async restoreVersion(
    docId: string,
    versionId: string,
  ): Promise<{ versionId: string }> {
    return api.post<{ versionId: string }>(`/docs/${docId}/restore`, {
      versionId,
    });
  },

  // ---- sharing ----
  async createShare(
    docId: string,
    expiresAt: number | null,
  ): Promise<ShareResponse> {
    return api.post<ShareResponse>("/shares", { docId, expiresAt });
  },
  async resolveShare(shareId: string): Promise<ResolveShareResponse> {
    return api.get<ResolveShareResponse>(`/shares/${shareId}`);
  },
  async deleteShare(shareId: string): Promise<void> {
    await api.del(`/shares/${shareId}`);
  },

  // ---- content download (scene JSON) ----
  async fetchSceneJSON(url: string): Promise<string> {
    return getTextFromPresigned(url);
  },
};
